import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Toolbar,
  Typography,
  IconButton,
  Stack,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowRightTwoToneIcon from "@mui/icons-material/ArrowRightTwoTone";
import ArrowLeftTwoToneIcon from "@mui/icons-material/ArrowLeftTwoTone";
import { getFiles, downloadFile } from "../api";
import AttachmentTable from "./AttachmentTable";
import PayrollsPopup from "./PayrollPopup";
import { useAlerts } from "../Hooks";
import { Loader } from "./Loader";

const AttachmentPopup = ({ open, onClose, row, hideHeader = false }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const {
    popup: alertPopup,
    showErrorPopup,
    hidePopup,
  } = useAlerts();

  const [rightPanelWidth, setRightPanelWidth] = useState(50);
  const containerRef = useRef(null);
  const isResizing = useRef(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);

  const fetchFiles = useCallback(async () => {
    if (!row?.id) return;
    setLoading(true);
    try {
      const response = await getFiles(row.id);
      if (
        (response?.StatusCode === 200 || response?.Status === "File found") &&
        response.Files
      ) {
        const validFiles = response.Files.filter((f) => {
          const name = (f.Filename || f.FileName || "").toLowerCase();
          return (
            !name.endsWith(".xls") &&
            !name.endsWith(".xlsx") &&
            !name.endsWith(".csv") &&
            !name.endsWith(".doc") &&
            !name.endsWith(".docx") &&
            !name.endsWith(".txt")
          );
        }).map((f) => ({
          id: f.AwsPath,
          name: f.Filename,
          url: f.AwsPath,
          isExisting: true,
        }));
        setAttachments(validFiles);
        setCurrentAttachmentIndex(0);
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error("Failed to fetch files", error);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [row?.id]);

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open, fetchFiles]);

  const currentFile = attachments[currentAttachmentIndex];
  let activeIframeSrc = currentFile?.url || "";
  if (
    activeIframeSrc &&
    activeIframeSrc.toLowerCase().includes(".pdf") &&
    !activeIframeSrc.includes("#")
  ) {
    activeIframeSrc += "#toolbar=1&view=FitH&scrollbar=1";
  }
  const showPreviewPanel = attachments.length > 0;

  const resize = useCallback((e) => {
    if (!isResizing.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newLeftWidth = e.clientX - containerRect.left;
    const containerWidth = containerRect.width;
    let newRightWidthPercent = 100 - (newLeftWidth / containerWidth) * 100;
    if (newRightWidthPercent < 30) newRightWidthPercent = 30;
    if (newRightWidthPercent > 70) newRightWidthPercent = 70;
    setRightPanelWidth(newRightWidthPercent);
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", resize);
    document.removeEventListener("mouseup", stopResizing);
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => (iframe.style.pointerEvents = "auto"));
  }, [resize]);

  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", stopResizing);
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => (iframe.style.pointerEvents = "none"));
  }, [resize, stopResizing]);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  const handleDownload = async (file, shouldManageLoading = true) => {
    if (!file.isExisting) return;
    const payload = { ReferenceID: row?.id, FileName: file.name };
    if (shouldManageLoading) setActionLoading(true);
    try {
      const response = await downloadFile(payload);
      if (
        response?.StatusCode === 200 &&
        response?.File?.Files?.[0]?.FileContent
      ) {
        const fileData = response.File.Files[0];
        const base64Content = fileData.FileContent;
        const byteCharacters = atob(base64Content);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileData.FileName || file.name);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        showErrorPopup("Download failed or file content missing");
      }
    } catch (error) {
      showErrorPopup("Download failed");
    } finally {
      if (shouldManageLoading) setActionLoading(false);
    }
  };

  const handlePreview = (file) => {
    const index = attachments.findIndex((f) => f.id === file.id);
    if (index > -1) {
      setCurrentAttachmentIndex(index);
    }
  };

  const handleDownloadAll = async () => {
    setActionLoading(true);
    try {
      const downloadPromises = attachments.filter(f => f.isExisting).map(file => handleDownload(file, false));
      await Promise.all(downloadPromises);
    } finally {
      setActionLoading(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      {!hideHeader && (
        <Toolbar sx={{ backgroundColor: "#F8F9FA", minHeight: '37px !important', gap: 1 }}>
          <Typography sx={{ flex: 1 }} component="div" fontWeight={600} fontSize={"16px"}>
            Attachments Details
          </Typography>
          <IconButton
            size="small"
            edge="start"
            onClick={onClose}
            style={{ backgroundColor: "#686868", borderRadius: 2, width: 20, height: 20 }}
            disableRipple
          >
            <CloseIcon sx={{ color: "white", fontSize: "11px" }} />
          </IconButton>
        </Toolbar>
      )}
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {(loading || actionLoading) && <Loader loading={loading || actionLoading} />}
        <Stack
          ref={containerRef}
          direction="row"
          sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <Stack
            gap={2}
            p={2}
            sx={{
              overflowY: "auto",
              height: "100%",
              width: showPreviewPanel ? `${100 - rightPanelWidth}%` : "100%",
            }}
          >
            {attachments.length > 0 && (
              <Stack direction="row" justifyContent="flex-end" mb={-1}>
                <Button
                  size="small"
                        color="secondary"
                        variant="contained"
                        disableElevation
                  onClick={handleDownloadAll}
                  disabled={actionLoading || loading}
                  endIcon={<i className="bi bi-link-45deg" style={{ fontSize: "12px" }}></i>}
                  sx={{ fontSize: "11px", textTransform: "none", height: 28 }}
                >
                  Download All
                </Button>
              </Stack>
            )}
            <AttachmentTable
              files={attachments}
              onPreview={handlePreview}
              onDownload={handleDownload}
              sx={{ width: "100%" }}
            />
          </Stack>

          {showPreviewPanel && (
            <Box
              onMouseDown={startResizing}
              sx={{
                width: "10px",
                cursor: "col-resize",
                bgcolor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderLeft: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                "&:hover": { bgcolor: "#e0e0e0" },
              }}
            >
              <Box sx={{ width: "2px", height: "20px", bgcolor: "#bdbdbd", mx: "1px" }} />
            </Box>
          )}

          {showPreviewPanel && (
            <Box
              sx={{
                height: "100%",
                width: `${rightPanelWidth}%`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {attachments.length > 1 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, bgcolor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                  <Button size="small" variant="contained" disabled={currentAttachmentIndex === 0} onClick={() => setCurrentAttachmentIndex((prev) => prev - 1)} startIcon={<ArrowLeftTwoToneIcon />} sx={{ textTransform: "none", bgcolor: "#3D5C7B", color: "white", fontSize: 12, "& .MuiButton-startIcon": { marginRight: "0" }, }}>
                    Previous
                  </Button>
                  <Typography variant="caption" fontWeight={500}>
                    {currentAttachmentIndex + 1} of {attachments.length} - {currentFile?.name || "Document"}
                  </Typography>
                  <Button size="small" variant="contained" disabled={currentAttachmentIndex === attachments.length - 1} onClick={() => setCurrentAttachmentIndex((prev) => prev + 1)} endIcon={<ArrowRightTwoToneIcon />} sx={{ textTransform: "none", bgcolor: "#3D5C7B", color: "white", fontSize: 12, "& .MuiButton-endIcon": { marginLeft: "0" }, }}>
                    Next
                  </Button>
                </Stack>
              )}
              <iframe
                src={activeIframeSrc}
                style={{ width: "100%", height: "100%", border: "none", flexGrow: 1 }}
                title= "Attachment Preview"
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <PayrollsPopup open={alertPopup.open} popupType={alertPopup.type} message={alertPopup.message} onConfirm={alertPopup.onConfirm} onCancel={hidePopup} />
    </Dialog>
  );
};

export default AttachmentPopup;